import { Box, Stack, Text } from "@mantine/core";
import React, { type JSX, useEffect, useRef, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";

import type { EligibleProduct, IntegratedSalesSteps } from "../../../types";

import { useSalesContext } from "../../../sales-context/SalesContext";
import { sendSalesErrorMetric } from "../../../shared-utils";
import { useAnalytics } from "../../../shared-utils/analytics/withPolymorphicClickAnalytics";
import { useIntegratedFlowStore, useProductOfferStore, useVerifiedCustomerStore } from "../../../stores";
import { getEnrollmentEntryStep } from "../utils/getEnrollmentEntryStep";
import { isNoChecklistProduct } from "../utils/isNoChecklistProduct";
import { CollectCustomerInfo } from "./CollectCustomerInfo/CollectCustomerInfo";
import { FullChecklistErrorFallback } from "./FullOfferChecklist/components/OfferChecklistFallbacks";
import { FullOfferChecklist } from "./FullOfferChecklist/FullOfferChecklist";
import { InlineSaleTrackerStep } from "./InlineSaleTracker/InlineSaleTrackerStep";
import { useLanguageStore } from "./language-selector/useLanguageStore";
import { SendLink } from "./SendLink/SendLink";
import { FullOfferChecklistStep } from "./StepSpecific/FullOfferChecklistStep";
import { SalesFooter } from "./StepSpecific/SalesFooter";
import { WaitForEnrollment } from "./StepSpecific/WaitForEnrollment";

export function IntegratedFlow({ product }: { product: EligibleProduct }): JSX.Element | null {
    const { sessionId, logger, taskType, partner, features } = useSalesContext();
    const { dispatcher } = useAnalytics();

    const { customer } = useVerifiedCustomerStore((state) => state.getVerifiedCustomer(sessionId));
    const [contactMDN, setContactMDN] = useState(customer?.mdn ?? "");
    const [contactEmail, setContactEmail] = useState(customer?.email ?? "");

    const languageState = useLanguageStore();

    const step: IntegratedSalesSteps = useIntegratedFlowStore((state) => state.getCurrentStep(sessionId));

    const primaryProductInfo = useProductOfferStore
        .getState()
        .getProductInfo(sessionId, product.productSku, languageState.language);

    const productHasNoChecklist = isNoChecklistProduct(primaryProductInfo);
    const isOnSuggestedOfferStep = step === "SuggestedOfferStep";

    // The step actually rendered — which can differ from the raw store `step`. The checklist only
    // controls whether the guided SuggestedOfferStep renders; it does NOT decide enroll-live vs.
    // track-externally. When a product has no checklist:
    //   - a non-primary product (e.g. Optimum) ALWAYS tracks externally, regardless of the current
    //     step. This must hold even if a stale step from a previous product/session lingers in the
    //     per-session step store — the RDESQ-2004 / PR #6075 guarantee is "no checklist ⇒ never
    //     CollectCustomerInfoStep/SendLinkStep", not just "not on initial entry".
    //   - a primary product (e.g. MCAFEEPCCARE) skips the checklist-dependent SuggestedOfferStep
    //     only on its arrival there, entering at the step its flags dictate (getEnrollmentEntryStep).
    //     Once advanced past entry, the requested step is honored so it can progress
    //     CollectCustomerInfo → SendLink → WaitingForEnrollment like normal.
    // Computed up here (not next to the render) so the analytics dispatch below keys off the step
    // that's actually shown — otherwise a no-checklist primary emits a phantom SuggestedOfferStep
    // funnel event on first entry before the store syncs to the real entry step (#6840).
    const effectiveStep: IntegratedSalesSteps = !productHasNoChecklist
        ? step
        : product.isPrimaryProduct
          ? isOnSuggestedOfferStep
              ? getEnrollmentEntryStep(product)
              : step
          : "InlineSaleTrackerStep";

    // A no-checklist *primary* product skips the checklist-dependent "SuggestedOfferStep" and
    // enters directly at its flag-derived entry step (getEnrollmentEntryStep). Two problems fall
    // out of that, both handled by the effect below via a per-(session, product) memory of the
    // last raw step:
    //   1. First-entry dead Back (#6840). On genuine first arrival the *store* step is still the
    //      default "SuggestedOfferStep" while the screen renders the entry step (via effectiveStep
    //      below). Both footers request "SuggestedOfferStep" on Back — which equals the current
    //      store value, so the write is a no-op, nothing re-renders, and Back is a dead button
    //      until the expert has advanced past entry. Fix: on first arrival, sync the store to the
    //      entry step so the raw step matches what's shown — then a Back actually changes it.
    //   2. Back trap after entering. Once synced/advanced, a Back that lands the raw step back on
    //      "SuggestedOfferStep" can only be a real Back click (the product never legitimately sits
    //      there). Remapping forward again would just re-render the screen they left, trapping
    //      them — so instead exit the guided sub-flow (deselect); they can reselect to re-enter.
    const previousEntryRef = useRef<{ sessionId: string; sku: string; step: IntegratedSalesSteps } | undefined>(
        undefined,
    );

    useEffect(() => {
        const previousEntry = previousEntryRef.current;
        previousEntryRef.current = { sessionId, sku: product.productSku, step };

        if (!(productHasNoChecklist && product.isPrimaryProduct && isOnSuggestedOfferStep)) {
            return;
        }

        const cameFromADifferentStepOnSameProduct =
            previousEntry?.sessionId === sessionId &&
            previousEntry.sku === product.productSku &&
            previousEntry.step !== "SuggestedOfferStep";

        if (cameFromADifferentStepOnSameProduct) {
            // Real Back navigation onto the non-existent SuggestedOfferStep → exit the sub-flow.
            useProductOfferStore.getState().updateSelectedProduct(sessionId, undefined);
        } else {
            // Genuine first arrival → sync the raw step to the entry step so a later Back is a real
            // transition (otherwise the footers' Back is a no-op write and stays a dead button).
            useIntegratedFlowStore.getState().updateIntegratedStep(sessionId, getEnrollmentEntryStep(product));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productHasNoChecklist, product.isPrimaryProduct, product.productSku, isOnSuggestedOfferStep, sessionId, step]);

    // Dispatch off effectiveStep (the rendered step), not the raw store `step` — so the funnel
    // event always matches the screen shown. On a no-checklist primary's first entry the raw step
    // is still "SuggestedOfferStep" for one commit; keying off effectiveStep emits the true entry
    // event (e.g. CollectCustomerInfoStep) once, with no phantom SuggestedOfferStep event (#6840).
    useEffect(() => {
        void dispatcher.dispatchBusinessEvent(`${effectiveStep}_IntegratedFlow`);
    }, [effectiveStep, dispatcher]);

    if (!taskType.isVoice && !taskType.isChat) {
        return null;
    }

    if (!primaryProductInfo) {
        logger.warn({ product, primaryProductInfo }, "Unable to find primary product info");
        return <Text>An error has occurred while trying to find plan details</Text>;
    }

    const isGuidedBrowsing = features.isGuidedBrowsingEnabled && !!primaryProductInfo.guidedBrowsing;

    const renderStepContent = (salesStep: IntegratedSalesSteps) => {
        switch (salesStep) {
            case "SuggestedOfferStep":
                return (
                    <ErrorBoundary
                        onError={(error) => {
                            logger.error({ error }, "Error rendering full offer checklist");
                            void sendSalesErrorMetric("FullOfferChecklistError", {
                                eventSource: "Offer-Checklist",
                                partner,
                            });
                        }}
                        fallbackRender={() => <FullChecklistErrorFallback />}
                    >
                        <FullOfferChecklistStep
                            product={product}
                            salesChecklist={primaryProductInfo?.salesOfferChecklist}
                            quickRebuttal={primaryProductInfo?.quickRebuttal}
                            sendLinkCopy={primaryProductInfo?.guidedBrowsing?.sendLinkCopy}
                            sendLinkSubCopy={primaryProductInfo?.guidedBrowsing?.sendLinkSubCopy}
                            sentSuccessMessage={primaryProductInfo?.guidedBrowsing?.sentSuccessMessage}
                            isGuidedBrowsing={isGuidedBrowsing}
                            checklistPlacement={primaryProductInfo?.guidedBrowsing?.checklistPlacement}
                            requiresChecklistCompletion={
                                primaryProductInfo?.guidedBrowsing?.requiresChecklistCompletion
                            }
                            returnLinkToExpert={primaryProductInfo?.guidedBrowsing?.returnLinkToExpert}
                            sendLinkCommType={primaryProductInfo?.sendLinkCommType}
                        />
                    </ErrorBoundary>
                );
            case "CollectCustomerInfoStep":
                return <CollectCustomerInfo />;
            case "WaitingForEnrollmentStep":
                return <WaitForEnrollment product={product} />;
            case "SendLinkStep":
                return (
                    <SendLink
                        product={product}
                        mdn={contactMDN}
                        email={contactEmail}
                        updateMDN={setContactMDN}
                        updateEmail={setContactEmail}
                        sendLinkCommTypeOverride={primaryProductInfo?.sendLinkCommType}
                        helpButtonEnabled={!!primaryProductInfo?.additionalHelpSteps}
                        isGuidedBrowsing={isGuidedBrowsing}
                    />
                );
            case "HelpCustomerFindSubscribeButtonStep":
                return (
                    <Stack>
                        <Box flex={1}>
                            {primaryProductInfo?.additionalHelpSteps ? (
                                <FullOfferChecklist
                                    salesChecklist={primaryProductInfo.additionalHelpSteps}
                                    product={product?.productInfo}
                                    productSku={product?.productSku}
                                />
                            ) : (
                                <Text>Unable to find locate steps. Please file a support ticket</Text>
                            )}
                        </Box>
                        <SalesFooter
                            onBack={() =>
                                useIntegratedFlowStore.getState().updateIntegratedStep(sessionId, "SendLinkStep")
                            }
                            continueButtonProps={{ nextStep: "InlineSaleTrackerStep" }}
                            product={product}
                        />
                    </Stack>
                );
            case "InlineSaleTrackerStep":
                return (
                    <InlineSaleTrackerStep
                        productInfo={primaryProductInfo}
                        product={product}
                        productInfoLink={primaryProductInfo?.productInfoLink}
                    />
                );
            default:
                return <></>;
        }
    };

    return renderStepContent(effectiveStep);
}
